import { AsyncPipe, KeyValuePipe, NgClass, NgFor, NgIf } from '@angular/common';
import { Component, Input } from '@angular/core';
import { Form, FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatOptionModule } from '@angular/material/core';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ConfirmDialogService } from '@igo2/common/confirm-dialog';
import { CustomHtmlComponent } from '@igo2/common/custom-html';
import { SpinnerComponent } from '@igo2/common/spinner';
import { ToolComponent } from '@igo2/common/tool';
import { WorkspaceStore } from '@igo2/common/workspace';
import { ConfigService } from '@igo2/core/config';
import { IgoLanguageModule, LanguageService } from '@igo2/core/language';
import { MessageService } from '@igo2/core/message';
import { StorageService } from '@igo2/core/storage';
import { ArcGISRestDataSource, DownloadService, IgoMap, ImageArcGISRestDataSource, isLayerGroup, Layer, LayerService, StyleListService, StyleService, VectorLayer, WMSDataSource } from '@igo2/geo';
import { MapState, WorkspaceState } from '@igo2/integration';
import { BehaviorSubject, skipWhile, Subscription } from 'rxjs';
import { SigtExtractByPolygonService } from 'src/app/shared/services/sigt-extract-by-polygon.service';

@ToolComponent({
  name: 'sigt-extract-by-polygon',
  title: 'Extraction par polygone',
  icon: 'feature_search'
})

@Component({
    selector: 'app-sigt-extract-by-polygon',
    imports: [
        MatButtonToggleModule,
        NgIf,
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
        NgFor,
        MatOptionModule,
        MatTooltipModule,
        MatButtonModule,
        SpinnerComponent,
        MatSlideToggleModule,
        MatInputModule,
        AsyncPipe,
        IgoLanguageModule
    ],
    templateUrl: './sigt-extract-by-polygon.component.html',
    styleUrl: './sigt-extract-by-polygon.component.scss'
})


export class SigtExtractByPolygonComponent {
  
  public form: UntypedFormGroup;
  extractableLayers$ =  new BehaviorSubject<Layer[]>([]);
  selectionLayers$ =  new BehaviorSubject<Layer[]>([]);
  public loading$ = new BehaviorSubject(false);

  private layers$$: Subscription;
  private extractableLayers$$: Subscription;
  private selectionLayers$$: Subscription;

  private requestSize: number;
  get selectionLayer() {
    return this.form.get('selectionLayer').value;
  }
  set selectionLayer(value) {
    this.form.patchValue({ selectionLayer: value });
  }
  get sourceLayer() {
    return this.form.get('sourceLayer').value;
  }
  set sourceLayer(value) {
    this.form.patchValue({sourceLayer: value});
  }

  get map(): IgoMap {
    return this.mapState.map;
  }

  get workspaceStore(): WorkspaceStore {
    return this.workspaceState.store;
  }

  constructor(
    private extractionService: SigtExtractByPolygonService,
    private mapState: MapState,
    private workspaceState: WorkspaceState,
    private languageService: LanguageService,
    private messageService: MessageService,
    private styleListService: StyleListService,
    private styleService: StyleService,
    private formBuilder: UntypedFormBuilder,
    private config: ConfigService,
    private storageService: StorageService,
    private downloadService: DownloadService,
    private layerService: LayerService,
    private confirmDialogService: ConfirmDialogService
  ) {
    this.loadConfig();
    this.buildForm();
  }

  ngOnInit() {
    this.layers$$ = this.map.layerController.all$.subscribe((layers) => {
      const extractableLayers = layers.filter((layer) => {
        if (isLayerGroup(layer)) {
          return false;
        }
        return (
          (layer instanceof VectorLayer && layer.exportable === true) ||
          (layer.dataSource instanceof WMSDataSource && layer.dataSource.options.urlWfs !== undefined) ||
          (layer.dataSource instanceof WMSDataSource && (<any> layer.dataSource.options).urlEsri !== undefined) ||
          (layer.dataSource instanceof ImageArcGISRestDataSource && (<any> layer.dataSource.options).urlEsri !== undefined) ||
          (layer.dataSource instanceof ArcGISRestDataSource)
        );
      });
      this.extractableLayers$.next(extractableLayers as Layer[]);
      const selectionLayers = layers.filter((layer)=>{
        if (isLayerGroup(layer)) {
          return false;
        }
        return (
          (layer instanceof VectorLayer 
            && !layer.dataSource.ol.isEmpty() && 
            ((layer.dataSource.ol.getFeatures()[0].getGeometry().getType()==="Polygon")||
            (layer.dataSource.ol.getFeatures()[0].getGeometry().getType()==="MultiPolygon") ||
            ((layer.dataSource.ol.getFeatures()[0].getGeometry().getType()==="Point") && 
             (layer.dataSource.ol.getFeatures()[0].get('rad') !== undefined))
          )
        ));        
      });
      this.selectionLayers$.next(selectionLayers as Layer[]);

    });

    this.selectionLayers$$ = this.selectionLayers$
      .pipe(skipWhile((layers) => !layers))
      .subscribe((layers) => {
        if (layers.length === 1) {
          this.form.patchValue({ selectionLayer: layers[0].id });
        }
      });
  }  
  
  private loadConfig() {
    const requestSizeConfig = this.config.getConfig('extractByPolygon.requestSize');
    this.requestSize =
      requestSizeConfig !== undefined ? requestSizeConfig : 1000;
  }
  private buildForm() {

    const form = this.formBuilder.group({
      sourceLayer: [, [Validators.required]],
      selectionLayer: [, [Validators.required]]
    });

    this.form = form;
  }

  handleFormSubmit(form:any) {
    const sourceLayer = this.getLayerById(form.sourceLayer);
    const selectionLayer = this.getLayerById(form.selectionLayer) as VectorLayer;
    this.extractionService.doExtract(sourceLayer, selectionLayer);
  }
  getLayerById(layerId:string):Layer {
    return <Layer> this.map.layerController.all.find((layer)=>{
      if(isLayerGroup(layer))return false;
      return layer.id === layerId;
    });
  }

}
